import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import type { ComponentProps, KeyboardEvent, ReactNode } from "react";

import { cn } from "helpers/cn";

const inputVariants = cva(
  "flex min-w-0 border border-input bg-card aria-invalid:border-destructive text-foreground transition-colors placeholder:text-subtle focus-visible:border-ring focus-visible:shadow-focus focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60",
  {
    variants: {
      size: {
        small: "h-6 rounded-md px-1.75 text-body-small",
        default: "h-8 rounded-lg px-3 text-body-small",
        large: "h-10 rounded-[10px] px-2.75 text-body",
        touch: "h-11 rounded-lg px-3.5 text-body"
      },
      width: {
        full: "w-full",
        compact: "w-22.5 shrink-0"
      },
      attached: {
        start: "relative rounded-e-none focus-within:z-1 focus-visible:z-1",
        end: "relative -ms-px rounded-s-none focus-within:z-1 focus-visible:z-1"
      }
    },
    defaultVariants: { size: "default", width: "full" }
  }
);

type Props = Omit<ComponentProps<"input">, "size" | "prefix"> &
  VariantProps<typeof inputVariants> & {
    prefix?: ReactNode;
    onPressEnter?: (event: KeyboardEvent<HTMLInputElement>) => void;
  };

const Input = ({
  size,
  width,
  attached,
  prefix,
  className,
  style,
  onKeyDown,
  onPressEnter,
  ...props
}: Props) => {
  const field = (
    <input
      className={
        prefix == null
          ? cn(inputVariants({ size, width, attached }), className)
          : "h-full w-full min-w-0 flex-1 bg-transparent p-0 outline-none placeholder:text-subtle disabled:cursor-not-allowed"
      }
      data-slot="input"
      style={prefix == null ? style : undefined}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.key === "Enter" && !event.repeat && !event.nativeEvent.isComposing) {
          onPressEnter?.(event);
        }
      }}
      {...props}
    />
  );

  if (prefix == null) return field;

  return (
    <label
      className={cn(
        inputVariants({ size, width, attached }),
        "cursor-text items-center gap-1 focus-within:border-ring focus-within:shadow-focus has-[input:disabled]:cursor-not-allowed has-[input:disabled]:bg-muted has-[input:disabled]:opacity-60 has-[input[aria-invalid=true]]:border-destructive",
        className
      )}
      data-slot="input-group"
      style={style}
    >
      <span aria-hidden className="shrink-0">
        {prefix}
      </span>
      {field}
    </label>
  );
};

export { Input };
